import { useMutation } from '@tanstack/react-query';
import { toast } from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';

import { queryClient } from '../../shared/providers/react-query-provider';
import { HttpError } from '../shared/api/client';
import { datadogLogger } from '../../shared/datadog/logger';

import {
    cancelAppointment,
    connectCalendar,
    disconnectCalendar,
    putAvailabilityConfig,
} from './api';
import {
    getAvailabilityConfigQueryKey,
    getCalendarStatusQueryKey,
    getDossierAppointmentsQueryKey,
} from './queries';
import { AvailabilityConfigPayload, CalendarProvider, ConnectCalendarPayload } from './types';

export const useConnectCalendarMutation = () =>
    useMutation({
        mutationFn: (payload: ConnectCalendarPayload) => connectCalendar(payload),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: getCalendarStatusQueryKey() });
        },
    });

export const useDisconnectCalendarMutation = () =>
    useMutation({
        mutationFn: (provider: CalendarProvider) => disconnectCalendar(provider),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: getCalendarStatusQueryKey() });
        },
    });

export const useCancelAppointmentMutation = (dossierId: string) => {
    const { formatMessage } = useIntl();
    return useMutation({
        mutationFn: (appointmentId: string) => cancelAppointment(appointmentId),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: getDossierAppointmentsQueryKey(dossierId),
            });
        },
        onError: () => {
            toast.error(formatMessage({ id: 'global.generic_error' }));
        },
    });
};

export const usePutAvailabilityConfig = () => {
    const { formatMessage } = useIntl();
    return useMutation({
        mutationFn: (payload: AvailabilityConfigPayload) => putAvailabilityConfig(payload),
        onError: (error) => {
            toast.error(formatMessage({ id: 'global.generic_error' }));
            if (error instanceof HttpError && error.status === 422) {
                datadogLogger.error(
                    'appointment: availability config validation error (422) — frontend/backend rules out of sync',
                    { errorMessage: error.message },
                );
                void queryClient.invalidateQueries({ queryKey: getAvailabilityConfigQueryKey() });
            }
        },
    });
};
